<template>
  <div class="search-skus-base">
    <div class="search-condition">
      <BaseSearchConditionModal
          :keyword="keyword"
          :is-and-search="isAndSearch"
          :conditions="conditions"
          :store-path="storePath"
          :res-path="responsePath"
          :save-key="saveKey"
          :base-query="null"
          :is-validation-only-condition="true"
          @search="search" />
    </div>
    <BaseTableList
        class="table"
        height="500"
        row-class-name="clickable"
        :table-set-list="skuSetList"
        :display-items="showSkuData"
        @row-dblclick="onRowDoubleClick">
      <template slot="title">
        <div class="table-header">
          <h3 class="table_title">検索結果</h3>
          <h4 v-show="showSkuDataCount"
              class="table_count">
            {{ skuDataCount }} 件
          </h4>
        </div>
      </template>
      <template slot="slots"
          slot-scope="{ row }">
        <slot :name="'slots'"
            :row="row" />
      </template>
    </BaseTableList>
    <!-- ページネーション -->
    <div class="base-pagination">
      <el-pagination
          :current-page="currentPage"
          :page-size="100"
          :total="skuData.length"
          background
          layout="prev, pager, next"
          @current-change="onPaginationCurrentChange" />
    </div>
  </div>
</template>

<style scoped>
.search-condition {
  margin: 5px 15px;
}
.search-condition .input-label {
  font-size: 14px;
  font-weight: bold;
  color: #9da6ba;
  text-align: right;
  padding-right: 10px;
}
.search-condition .search_button {
  text-align: right;
}
.el-row {
  margin: 5px 0;
}
.table_title {
  float: left;
}
.table_count {
  float: right;
}

.keywordInput {
  width: 250px;
}

.search-condition >>> .base-search-condition > div {
  width: 400px;
}

.base-pagination {
  margin-bottom: 10px;
}

</style>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'

import common from '@/utils/common'
import message from '@/utils/message'

import * as orderQuery from '@/queries/order'

import BaseTableList from '@/components/molecules/BaseTableList'
import BaseSearchConditionModal from '@/components/organisms/BaseSearchConditionModal'
import * as productSearchQuery from '@/queries/product'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'

export default {
  name: 'SearchSkus',
  components: {
    BaseTableList,
    BaseSearchConditionModal
  },
  inject: ['parentValidator'],
  props: {
    skuCode: {
      type: String,
      default: null
    },
    isFromProduct: {
      type: Boolean,
      default: false
    },
    tableSetList: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      totalCountResponsepath: '',
      responsepath: '',
      currentPage: 1,
      searchTarget: 0,
      searchText: '',
      skuData: []
    }
  },
  computed: {
    ...mapState({
      productConditions: (state) => state.ProductDetail.searchConditions,
      allConditions: (state) => state.convertedSearchConditions
    }),
    ...mapGetters({
      searchConditions: 'searchConditions'
    }),
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode',
      orderDatetime: 'orderDatetime'
    }),
    ...mapGetters({
      componentSelectableSkuType: 'componentSelectableSkuType',
      exchangeSelectableSkuType: 'exchangeSelectableSkuType'
    }),
    skuSetList () {
      return this.tableSetList || [
        {
          type: 'String', label: this.isFromProduct ? '商品/SKUコード' : 'SKU',
          path: 'code', width: 200
        },
        {
          type: 'String', label: '商品名', elementType: ELEMENT_TYPE.FUNCTION,
          path: (row) => {
            const skuName = _.get(row, 'name', '')
            if (skuName !== '') {
              return skuName
            } else {
              return _.get(row, 'product.name')
            }
          }
        }
      ]
    },
    skuDataCount () {
      return this.skuData.length
    },
    showSkuDataCount () {
      return this.skuData.length > 0
    },
    showSkuData () {
      const min = (this.currentPage - 1) * 100
      const max = (this.currentPage) * 100
      return this.skuData.filter((data, index) => index >= min && index < max)
    },
    storePath () {
      return 'ProductDetail'
    },
    store () {
      return this.$store.state[this.storePath] || {}
    },
    keyword () {
      return this.store.searchKeyword || ''
    },
    isAndSearch () {
      return this.store.isAndSearch || false
    },
    conditions () {
      let conditions = this.productConditions
      if (this.isFromProduct) {
        // 構成品の検索の固定の検索条件を設定
        conditions =
          {
            'searchTarget': {
              type: 'SingleSelector',
              title: '検索対象',
              display: true,
              value: 0,
              required: true,
              options: [
                { name: '品番', code: 0 },
                { name: 'SKU', code: 1 }
              ]
            }
          }
        Object.assign(conditions, this.productConditions)
        delete conditions['isContainComponent']
      } else {
        delete conditions['searchTarget']
      }
      this.$store.dispatch({
        type: 'set',
        states: {
          'ProductDetail/searchConditions': conditions
        }
      })

      // SKU種類が検索条件にある場合、サービスサイトで持っている検索対象のみ選択可能とする
      const editableSKU = (this.isFromProduct ? this.componentSelectableSkuType
        : this.exchangeSelectableSkuType) || []
      Object.keys(conditions).forEach((condition) => {
        if (condition === 'skuType') {
          const options = _.get(conditions[condition], 'options', [])
          _.set(conditions[condition], 'options',
            options.filter((option) => editableSKU.length === 0 ||
              editableSKU.includes(option.code)))
        }
      })

      return conditions
    },
    saveKey () {
      return 'productSearcherCondition'
    },
    responsePath () {
      // sku と 品番で異なる
      return 'data.data.skus.edges'
    }
  },
  watch: {
    skuCode (val) {
      this.searchText = val
      this.skuData = []
    }
  },
  created () {
    const key = this.isFromProduct ? 'product_list' : 'exchange_sku_search'
    this.$store.dispatch({
      type: 'set',
      states: {
        'ProductDetail.searchConditions': this.allConditions[key]
      }
    })
  },
  mounted () {
    this.searchText = this.skuCode
  },
  methods: {
    async search (keyword, conditions) {
      try {
        this.$loadingService.show()
        let query
        const searchTarget = _.get(conditions, 'searchTarget.value', null)
        let queryCondition = this.searchConditions(this.storePath)
        delete queryCondition.searchTarget // クエリには付与しない条件のため除去

        if (!('skuType' in queryCondition) && this.isFromProduct && this.componentSelectableSkuType.length > 0) {
          // SKU種類の指定がない場合、検索対象となるSKU種類を指定する
          _.set(queryCondition, 'skuType', this.componentSelectableSkuType)
        } else if (!('skuType' in queryCondition) && !this.isFromProduct && this.exchangeSelectableSkuType.length > 0) {
          // SKU種類の指定がない場合、検索対象となるSKU種類を指定する
          _.set(queryCondition, 'skuType', this.exchangeSelectableSkuType)
        }

        if (this.isFromProduct) {
          if (searchTarget === 0) {
            // 品番検索
            query = common.addGraphQlCondition(productSearchQuery.GRAPH_QL_QUERY_SEARCHER_PRODUCT_LIST, queryCondition)
          } else {
            // SKU検索
            query = common.addGraphQlCondition(productSearchQuery.GRAPH_QL_QUERY_SEARCHER_SKU_LIST, queryCondition)
          }
        } else {
          // SKU検索
          query = orderQuery.GRAPH_QL_QUERY_SKU_LIST
          query = common.addGraphQlCondition(query, Object.assign(queryCondition, { isActive: 1, isNotContainsBundle: 1 }))
          query = common.addGraphQlCondition(query, { siteCode: this.siteCode, baseDateTime: this.orderDatetime })
          query = common.addGraphQlCondition(query, { siteCode: this.siteCode, baseDateTime: this.orderDatetime })
        }

        const res = await common.postGraphQl(query)

        if (this.isFromProduct) {
          if (searchTarget === 0) {
            // 品番検索
            const totalCount = _.get(res, 'data.data.products.totalCount', 0)
            const listSize = _.chain(res).get('data.data.products.edges').size().value()

            if (totalCount !== listSize) {
              // 検索結果の件数が上限を超えた場合
              this.$notify.warning(message.MSG_306())
            }

            this.skuData = _.chain(res)
              .get('data.data.products.edges')
              .map(val => _.get(val, 'node'))
              .value()
          } else {
            // SKU検索
            const totalCount = _.get(res, 'data.data.skus.totalCount', 0)
            const listSize = _.chain(res).get('data.data.skus.edges').size().value()

            if (totalCount !== listSize) {
              // 検索結果の件数が上限を超えた場合
              this.$notify.warning(message.MSG_306())
            }

            this.skuData = _.chain(res)
              .get('data.data.skus.edges')
              .map(val => _.get(val, 'node'))
              .value()
          }
        } else {
          const totalCount = _.get(res, 'data.data.skus.totalCount', 0)
          const listSize = _.chain(res).get('data.data.skus.edges').size().value()
          if (totalCount !== listSize) {
            // 検索結果の件数が上限を超えた場合
            this.$notify.warning(message.MSG_306())
          }
          this.skuData = _.chain(res)
            .get('data.data.skus.edges')
            .map(val => val.node)
            .value()
        }

        // 結果が null の場合
        if (this.skuData == null) {
          this.$notify.error({
            message: 'データを取得できませんでした。'
          })
          return
        }
        this.currentPage = 1
      } catch (error) {
        const message = _.get(error, 'ccsErrors.0.message')
        if (message) {
          this.$notify.error({ message })
          return
        }
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    onRowDoubleClick (row, column, event) {
      this.$emit('sku-selected', row)
    },
    onPaginationCurrentChange (val) {
      this.currentPage = val
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/SearchSkus.vue